import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, Check, X } from 'lucide-react';

const plans = [
  {
    name: 'Free',
    price: '$0',
    features: [
      'Sync up to 10 playlists/month',
      'Max 10 tracks per playlist',
      'Basic support',
      'Community access',
      'Access on web and mobile',
      'Basic playlist matching',
      'Ad-supported',
    ],
    cta: 'Get Started',
    highlight: false,
  },
  {
    name: 'Pro',
    price: '$9/mo',
    features: [
      'Sync up to 50 playlists/month',
      'Max 100 tracks per playlist',
      'Priority support',
      'Advanced matching',
      'No ads',
      'Sync scheduling',
      'Playlist backup & restore',
      'Access on all devices',
      'Early access to new features',
    ],
    cta: 'Upgrade to Pro',
    highlight: true,
  },
  {
    name: 'Premium',
    price: '$19/mo',
    features: [
      'Unlimited playlist syncs',
      '500 tracks per playlist',
      '1-on-1 onboarding',
      'Early access to new features',
      'Dedicated account manager',
      'Priority bug fixes',
      'Custom sync rules',
      'Family sharing (up to 5 users)',
      'Exclusive webinars & events',
      'All Pro features included',
    ],
    cta: 'Go Premium',
    highlight: false,
  },
];

const faqs = [
  {
    question: 'Can I change my plan later?',
    answer: 'Yes! You can upgrade or downgrade your plan at any time from your account settings.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards, PayPal, and Google Pay.'
  },
  {
    question: 'Is there a free trial for Pro or Premium?',
    answer: 'Yes, you can try Pro or Premium free for 7 days. Cancel anytime during the trial.'
  },
  {
    question: 'What happens if I hit my playlist sync limit?',
    answer: 'You can upgrade your plan or wait until your monthly quota resets.'
  },
  {
    question: 'How do I contact support?',
    answer: 'All users can contact support via our Help Center. Pro and Premium users get priority responses.'
  },
];

// Feature comparison data
const featureList = [
  'Sync playlists/month',
  'Max tracks per playlist',
  'Get an overview of all your music collections in one interface',
  'Delete duplicate tracks in your playlists',
  'No ads',
  'Priority support',
  'Advanced matching',
  'Sync scheduling',
  'Playlist backup & restore',
  'Access on all devices',
  'Early access to new features',
  '1-on-1 onboarding',
  'Dedicated account manager',
  'Priority bug fixes',
  'Custom sync rules',
  'Family sharing',
  'Exclusive webinars & events',
];

const featureMap = {
  'Free': {
    'Sync playlists/month': '10',
    'Max tracks per playlist': '10',
    'Get an overview of all your music collections in one interface' : true,
    'Delete duplicate tracks in your playlists' : true,
    'No ads': false,
    'Priority support': false,
    'Advanced matching': false,
    'Sync scheduling': false,
    'Playlist backup & restore': false,
    'Access on all devices': true,
    'Early access to new features': false,
    '1-on-1 onboarding': false,
    'Dedicated account manager': false,
    'Priority bug fixes': false,
    'Custom sync rules': false,
    'Family sharing': false,
    'Exclusive webinars & events': false,
  },
  'Pro': {
    'Sync playlists/month': '50',
    'Max tracks per playlist': '100',
    'Get an overview of all your music collections in one interface' : true,
    'Delete duplicate tracks in your playlists' : true,
    'No ads': true,
    'Priority support': true,
    'Advanced matching': true,
    'Sync scheduling': true,
    'Playlist backup & restore': true,
    'Access on all devices': true,
    'Early access to new features': true,
    '1-on-1 onboarding': false,
    'Dedicated account manager': false,
    'Priority bug fixes': false,
    'Custom sync rules': false,
    'Family sharing': false,
    'Exclusive webinars & events': false,
  },
  'Premium': {
    'Sync playlists/month': 'Unlimited',
    'Max tracks per playlist': '500',
    'Get an overview of all your music collections in one interface' : true,
    'Delete duplicate tracks in your playlists' : true,
    'No ads': true,
    'Priority support': true,
    'Advanced matching': true,
    'Sync scheduling': true,
    'Playlist backup & restore': true,
    'Access on all devices': true,
    'Early access to new features': true,
    '1-on-1 onboarding': true,
    'Dedicated account manager': true,
    'Priority bug fixes': true,
    'Custom sync rules': true,
    'Family sharing': 'Up to 5 users',
    'Exclusive webinars & events': true,
  },
};

const Pricing = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const adRef = useRef(null);

  useEffect(() => {
    if (adRef.current) {
      const adLoaded = adRef.current.getAttribute("data-ad-status") === "filled";
      if (!adLoaded) {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          console.error("AdSense Error:", e);
        }
      }
    }
  }, []);


  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 pt-14 lg:pt-32">
      <div>
          <h1>Welcome to Playlist Sync</h1>
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-6539527697946100"
            data-ad-slot="1422093926"
            data-ad-format="auto"
            data-full-width-responsive="true"
          ></ins>
        </div>

      <h1 className="text-4xl font-bold mb-4 text-center text-primary">Pricing</h1>
      <p className="text-lg text-text-secondary mb-10 text-center max-w-xl">
        Choose the plan that fits your music syncing needs. Upgrade or downgrade anytime.
      </p>
      <div className="flex flex-col md:flex-row gap-8 w-full max-w-6xl justify-center mb-16">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative overflow-hidden flex-1 bg-surface border border-border rounded-xl shadow-lg p-8 flex flex-col items-center ${plan.highlight ? 'border-primary shadow-primary/20 md:scale-105' : ''}`}
          >
            {plan.highlight && (
              <div className="absolute left-0 top-[0px] z-20" style={{width: '90px', height: '90px', pointerEvents: 'none'}}>
                <div
                  className="absolute left-[-30px] top-4 bg-primary text-primary-foreground text-xs font-bold px-0 py-1 shadow-lg transform -rotate-45"
                  style={{
                    width: '130px',
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                    overflow: 'hidden',
                  }}
                >
                  <span style={{display: 'block', padding: '4px 0'}}>Most Popular</span>
                </div>
              </div>
            )}
            <h2 className="text-2xl font-bold mb-2 text-primary">{plan.name}</h2>
            <div className="text-3xl font-extrabold mb-4">{plan.price}</div>
            <ul className="mb-6 space-y-4 text-text-primary">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-center gap-3">
                  <span className="inline-block w-2 h-2 bg-primary rounded-full font-light"></span>
                  {feature}
                </li>
              ))}
            </ul>
            <button className={`w-full py-2 px-4 rounded-lg font-semibold transition-smooth ${plan.highlight ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-surface-secondary text-primary border border-primary hover:bg-primary/10'}`}>
              {plan.cta}
            </button>
          </div>
        ))}
      </div>
      <div className="w-full max-w-5xl bg-surface border border-border rounded-xl shadow-lg p-8 mt-16 overflow-x-auto">
        <h2 className="text-2xl font-bold mb-6 text-primary text-center">Compare Features</h2>
        <table className="w-full text-sm md:text-base border-collapse">
          <thead>
            <tr>
              <th className="text-left py-3 px-2 border-b border-border font-semibold text-text-secondary">Feature</th>
              {plans.map(plan => (
                <th key={plan.name} className="py-3 px-2 border-b border-border font-semibold text-primary text-center">{plan.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {featureList.map(feature => (
              <tr key={feature} className="border-b border-border">
                <td className="py-3 px-2 font-medium text-text-primary whitespace-nowrap">{feature}</td>
                {plans.map(plan => {
                  const value = featureMap[plan.name][feature];
                  return (
                    <td key={plan.name} className="py-3 px-2 text-center">
                      {value === true && <Check className="inline w-5 h-5 text-green-500" />}
                      {value === false && <X className="inline w-5 h-5 text-red-500" />}
                      {typeof value === 'string' && <span className="font-semibold text-primary">{value}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="w-full max-w-5xl bg-surface border border-border rounded-xl shadow-lg p-8 mt-16">
        <h2 className="text-2xl font-bold mb-6 text-primary text-center">Frequently Asked Questions</h2>
        <div className="space-y-6">
          {faqs.map((faq, idx) => (
            <div key={faq.question} className="border-b border-border pb-6">
              <button
                className="w-full text-left flex justify-between items-center text-lg font-semibold text-text-primary focus:outline-none group"
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                aria-expanded={openFaq === idx}
              >
                <span className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary/80" />
                  {faq.question}
                </span>
                <span className={`ml-2 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`}>
                  {openFaq === idx ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-40 opacity-100 mt-5' : 'max-h-0 opacity-0 mt-0'}`}
              >
                <div className="bg-background border border-border rounded-lg p-4 text-text-secondary text-base shadow animate-fade-in">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
     
    </div>
  );
};

export default Pricing; 